import React, { useEffect, useMemo, useState } from 'react';
import { ReturnQueue, ProcessReturn } from './components';
import { ReturnOrder, ReturnItem, ProcessReturnData } from './returnsTypes';
import { listTickets, getTicket, updateTicket } from '../../../services/ticketsApi';
import { Ticket } from '../../../services/ticketsApi';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { formatOrderId } from '../../../utils/formatId';
import styles from './WarehouseReturns.module.css';

function adaptOrder(t: Ticket): ReturnOrder {
  return {
    id: String(t.id),
    returnId: t.ticket_number,
    orderNumber: t.order_id ? formatOrderId(t.order_id) : '—',
    customerName: t.customer_name || '—',
    reason: t.subject,
    status: t.status === 'resolved' || t.status === 'closed' ? 'Completed' : 'Pending',
  };
}

// Parse the description left by the driver's CaptureReturnForm — each line:
//   "• <name> — qty <n>, <condition> · <notes>"
function parseLines(description: string): ReturnItem[] {
  if (!description) return [];
  return description
    .split('\n')
    .map((raw, idx) => raw.trim())
    .filter((line) => line.startsWith('•'))
    .map((line, idx) => {
      const body = line.replace(/^•\s*/, '');
      const [namePart, rest] = body.split('—').map((s) => s?.trim() ?? '');
      const qtyMatch = rest?.match(/qty\s+(\d+)/i);
      const conditionMatch = rest?.match(/qty\s+\d+,\s*([^·]+?)(?:\s*·|$)/i);
      const notesMatch = rest?.match(/·\s*(.+)$/);
      return {
        id: `it-${idx}`,
        productName: namePart || `Item ${idx + 1}`,
        sku: '',
        quantityReturned: qtyMatch?.[1] ?? '',
        condition: conditionMatch?.[1]?.trim() ?? '',
        restockToInventory: false,
        notes: notesMatch?.[1]?.trim() ?? '',
      };
    });
}

const WarehouseReturns: React.FC = () => {
  const [orders, setOrders] = useState<ReturnOrder[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [items, setItems] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listTickets({ category: 'returns', limit: 50 });
      const data = list.data ?? [];
      setTickets(data);
      setOrders(data.map(adaptOrder));
      // Auto-select first pending if nothing selected
      if (!selected && data.length > 0) {
        const first = data.find((t) => t.status !== 'resolved' && t.status !== 'closed') ?? data[0];
        const full = await getTicket(first.id);
        setSelected(full);
        setItems(parseLines(full.description ?? ''));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleItemChange = (id: string, updates: Partial<ReturnItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  const handleSelectReturn = async (returnId: string) => {
    const t = tickets.find((x) => String(x.id) === returnId);
    if (!t) return;
    try {
      const full = await getTicket(t.id);
      setSelected(full);
      setItems(parseLines(full.description ?? ''));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load return');
    }
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const summary = items
        .map((it) => `• ${it.productName} — qty ${it.quantityReturned}, ${it.condition}${it.restockToInventory ? ' · restocked' : ''}${it.notes ? ` · ${it.notes}` : ''}`)
        .join('\n');
      await updateTicket(selected.id, {
        status: 'resolved',
        resolution_notes: summary || 'Return processed',
      });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm return');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!selected) return;
    setItems(parseLines(selected.description ?? ''));
  };

  const detail: ProcessReturnData | null = useMemo(() => {
    if (!selected) return null;
    return {
      returnId: selected.ticket_number,
      orderNumber: selected.order_id ? formatOrderId(selected.order_id) : '—',
      customerName: selected.customer_name || '—',
      items,
    };
  }, [selected, items]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && orders.length === 0}
        error={error}
        empty={!loading && orders.length === 0}
        emptyMessage="No returns in the queue"
        onRetry={refresh}
      >
        <ReturnQueue orders={orders} onSelectReturn={handleSelectReturn} />
        {detail && (
          <ProcessReturn
            data={detail}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            onItemChange={handleItemChange}
          />
        )}
      </AsyncBoundary>
      {saving && <p style={{ textAlign: 'center', color: '#6B7280' }}>Saving…</p>}
    </div>
  );
};

export const WarehouseReturnsPage: React.FC = () => {
  return <WarehouseReturns />;
};

export default WarehouseReturns;
